import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import TutorialWizard, { TutorialMode } from '../components/Tutorial/TutorialWizard';
import { useAuth } from './AuthContext';
import { useDashboardDataContext } from './DashboardDataContext';

interface TutorialContextType {
  openTutorial: () => void;
}

const TutorialContext = createContext<TutorialContextType | null>(null);

/**
 * First-run prompt is only for accounts created after the tutorial shipped.
 * Existing counselors already know the app; they can still open it from the profile menu.
 * Set `users/{uid}.tutorialCompletedAt` to skip the prompt for any one person.
 */
const TUTORIAL_AUTO_PROMPT_AFTER_MS = Date.parse('2026-09-11T00:00:00.000Z');

export function useTutorial(): TutorialContextType {
  const context = useContext(TutorialContext);
  if (!context) throw new Error('useTutorial must be used within TutorialProvider');
  return context;
}

export const TutorialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, markTutorialCompleted } = useAuth();
  const { loading: dashboardLoading, newAssignmentModal } = useDashboardDataContext();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<TutorialMode>('welcome');
  const promptedUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (!currentUser) {
      promptedUserRef.current = null;
      setOpen(false);
      return;
    }
    if (
      location.pathname !== '/' ||
      dashboardLoading ||
      newAssignmentModal ||
      currentUser.tutorialCompletedAt ||
      currentUser.createdAt.getTime() < TUTORIAL_AUTO_PROMPT_AFTER_MS ||
      promptedUserRef.current === currentUser.id
    ) {
      return;
    }

    // Let dashboard notifications and the first layout paint settle before adding another overlay.
    const timer = window.setTimeout(() => {
      // Latched inside the timer, not before it: any dependency changing within these 700ms
      // cancels the timeout, and a guard set upfront would then swallow the prompt for good.
      promptedUserRef.current = currentUser.id;
      setMode('welcome');
      setOpen(true);
    }, 700);
    return () => window.clearTimeout(timer);
  }, [currentUser, dashboardLoading, newAssignmentModal, location.pathname]);

  const openTutorial = useCallback(() => {
    setMode('wizard');
    setOpen(true);
  }, []);

  /**
   * Closes first, persists after. A Firestore write stays pending until the server
   * acknowledges it — offline it never settles at all — so awaiting it here would hold the
   * overlay open, and the whole app unusable behind it, for as long as the connection is
   * blocked. If the write fails, `markTutorialCompleted` rolls its optimistic state back and
   * the prompt returns on the next eligible visit, which is the intended fallback anyway.
   */
  const dismiss = useCallback(() => {
    setOpen(false);
    void markTutorialCompleted().catch((error) => {
      console.error('Could not persist tutorial completion:', error);
    });
  }, [markTutorialCompleted]);

  return (
    <TutorialContext.Provider value={{ openTutorial }}>
      {children}
      <TutorialWizard
        open={open}
        mode={mode}
        userName={currentUser?.fullName ?? ''}
        onStart={() => setMode('wizard')}
        onDismiss={dismiss}
      />
    </TutorialContext.Provider>
  );
};
